import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Folder, Pencil, Trash2, X } from "lucide-react";
import { ApiClientError } from "@/api/client";
import {
  KbFolder,
  createKbFolder,
  deleteKbFolder,
  moveKbFolder,
  renameKbFolder,
} from "@/api/knowledge";

/**
 * D-147 W1 S5 — «Керувати папками» (owner-grade; §9/P11). Server-side
 * інваріанти: глибина ≤3 · без циклів · той самий розділ · непорожня
 * (item-ref у draft АБО published, АБО дочірні) → 409 «Спочатку перемістіть
 * матеріали» (AC-KB-21). FE лише показує помилки сервісу — жодних власних
 * правил.
 */
export function KbFoldersModal({
  sectionId,
  folders,
  onClose,
}: {
  sectionId: string;
  folders: KbFolder[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newParent, setNewParent] = useState("");
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const [movingId, setMovingId] = useState<string | null>(null);

  const byParent = useMemo(() => {
    const map = new Map<string | null, KbFolder[]>();
    for (const f of folders) {
      const key = f.parentFolderId;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(f);
    }
    return map;
  }, [folders]);

  const flat = useMemo(() => {
    const out: { folder: KbFolder; depth: number }[] = [];
    const walk = (parent: string | null, depth: number) => {
      for (const f of byParent.get(parent) ?? []) {
        out.push({ folder: f, depth });
        walk(f.id, depth + 1);
      }
    };
    walk(null, 0);
    return out;
  }, [byParent]);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["kbSection", sectionId] });

  const run = (fn: () => Promise<unknown>, notEmptyMsg = false) => {
    setBusy(true);
    setError(null);
    void fn()
      .then(invalidate)
      .catch((e: unknown) => {
        if (notEmptyMsg && e instanceof ApiClientError && e.status === 409) {
          setError("Папка непорожня — спочатку перемістіть матеріали.");
          return;
        }
        setError(e instanceof Error ? e.message : "Дія не вдалася.");
      })
      .finally(() => setBusy(false));
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Керувати папками"
    >
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl border bg-card shadow-2xl">
        <div className="flex items-center justify-between border-b px-5 py-3.5">
          <span className="text-sm font-bold">Керувати папками</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрити"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <p role="alert" className="mb-3 rounded-lg bg-destructive-soft px-3 py-2 text-xs text-destructive-soft-foreground">
              {error}
            </p>
          )}

          {flat.length === 0 && (
            <p className="mb-3 text-sm text-muted-foreground">Папок ще немає.</p>
          )}

          <ul className="mb-4 space-y-1">
            {flat.map(({ folder, depth }) => (
              <li
                key={folder.id}
                className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
                style={{ marginLeft: depth * 18 }}
              >
                {renaming?.id === folder.id ? (
                  <>
                    <input
                      value={renaming.name}
                      autoFocus
                      onChange={(e) => setRenaming({ id: folder.id, name: e.target.value })}
                      className="min-w-0 flex-1 rounded border bg-background px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-ring"
                    />
                    <button
                      type="button"
                      disabled={busy || renaming.name.trim() === ""}
                      aria-label="Зберегти назву"
                      onClick={() =>
                        run(async () => {
                          await renameKbFolder(folder.id, renaming.name.trim());
                          setRenaming(null);
                        })
                      }
                      className="rounded p-1 text-primary hover:bg-muted disabled:opacity-50"
                    >
                      <Check className="h-4 w-4" />
                    </button>
                  </>
                ) : movingId === folder.id ? (
                  <>
                    <span className="min-w-0 flex-1 truncate font-medium">{folder.name}</span>
                    <label className="text-xs text-muted-foreground">
                      До:{" "}
                      <select
                        disabled={busy}
                        defaultValue=""
                        onChange={(e) =>
                          run(async () => {
                            await moveKbFolder(folder.id, e.target.value || null);
                            setMovingId(null);
                          })
                        }
                        className="rounded border bg-background px-2 py-1 text-xs outline-none"
                      >
                        <option value="" disabled>
                          оберіть…
                        </option>
                        <option value="">— корінь —</option>
                        {folders
                          .filter((f) => f.id !== folder.id)
                          .map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.name}
                            </option>
                          ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      onClick={() => setMovingId(null)}
                      className="rounded p-1 text-muted-foreground hover:bg-muted"
                      aria-label="Скасувати перенос"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate font-medium">
                      <Folder className="h-4 w-4 flex-none text-muted-foreground" aria-hidden />
                      {folder.name}
                    </span>
                    <button
                      type="button"
                      disabled={busy}
                      aria-label={`Перейменувати ${folder.name}`}
                      onClick={() => setRenaming({ id: folder.id, name: folder.name })}
                      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => setMovingId(folder.id)}
                      className="rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      Перемістити
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      aria-label={`Видалити ${folder.name}`}
                      onClick={() => run(() => deleteKbFolder(folder.id), true)}
                      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>

          <div className="rounded-lg border border-dashed p-3">
            <div className="mb-1.5 text-xs font-semibold">Нова папка</div>
            <div className="flex flex-wrap gap-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Назва папки"
                className="min-w-[160px] flex-1 rounded-lg border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
              />
              <select
                value={newParent}
                onChange={(e) => setNewParent(e.target.value)}
                className="rounded-lg border bg-background px-2 py-1.5 text-sm outline-none"
              >
                <option value="">— корінь —</option>
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={busy || newName.trim() === ""}
                onClick={() =>
                  run(async () => {
                    await createKbFolder(sectionId, newName.trim(), newParent || null);
                    setNewName("");
                  })
                }
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
              >
                Створити
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end border-t px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border px-3.5 py-2 text-sm font-medium hover:bg-accent"
          >
            Готово
          </button>
        </div>
      </div>
    </div>
  );
}
